import { useCallback, useState } from "react";
import { Col, Form, FormGroup, FormLabel } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { warnAlert } from "../../../alert/defaultAlert";
import AsteriskRequired from "../../../Common/AsteriskRequired";
import PaymentMethodSelect from "../../Common/PaymentMethod/PaymentMethodSelect";
import PaymentMethod from "../../Common/PaymentMethod/PaymentMethod";
import CategorySelect from "../Category/CategorySelect";

/** Colaborador é pago em dinheiro ou Pix; o backend recusa as outras formas. */
const ACCEPTED_METHODS = [PaymentMethod.CASH, PaymentMethod.PIX];

/** Chave da última categoria usada, para o próximo pagamento já vir com ela. */
const LAST_CATEGORY_KEY = 'colmeia.employeePayment.lastCategory';

/** Hoje no formato do campo de data. */
const today = () => moment().format('YYYY-MM-DD');

/**
 * Última categoria escolhida neste navegador. É só conveniência: sem armazenamento disponível
 * (aba anônima, bloqueio do navegador) o campo apenas começa vazio.
 */
const readLastCategory = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(LAST_CATEGORY_KEY));
        return stored?.value ? stored : null;
    } catch {
        return null;
    }
};

/** Guarda a categoria usada; falha ao gravar não impede o pagamento. */
const storeLastCategory = (category) => {
    try {
        localStorage.setItem(LAST_CATEGORY_KEY, JSON.stringify({ value: category.value, label: category.label }));
    } catch {
        // Sem armazenamento a tela só deixa de lembrar a escolha.
    }
};

/**
 * Data, forma e categoria do pagamento do colaborador, com as regras de preenchimento.
 *
 * Compartilhado pelo pagamento de um colaborador e pelo "Pagar todos": os dois pedem os mesmos
 * três campos e precisam validar e lembrar a categoria do mesmo jeito.
 */
export const useEmployeePaymentFields = () => {
    const { t } = useTranslation();
    const [paymentDate, setPaymentDate] = useState(today());
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [category, setCategory] = useState(null);

    // Cada abertura começa com a data de hoje e a última categoria: a data de ontem não serve
    // para o pagamento de hoje, e a categoria quase nunca muda de um mês para o outro.
    const reset = useCallback(() => {
        setPaymentDate(today());
        setPaymentMethod(null);
        setCategory(readLastCategory());
    }, []);

    /** Avisa o primeiro campo faltando e diz se dá para enviar. */
    const validate = () => {
        if (!paymentDate) {
            warnAlert(t('cash_flow.employee_payment_date_required'));
            return false;
        }
        if (!paymentMethod?.value) {
            warnAlert(t('validation.required.payment_method'));
            return false;
        }
        if (!category?.value) {
            warnAlert(t('cash_flow.employee_payment_category_required'));
            return false;
        }
        return true;
    };

    /** Os três campos no formato que a API espera. */
    const toPayload = () => ({
        paymentDate,
        paymentMethod: paymentMethod?.value,
        category: category?.value ? { id: category.value } : null
    });

    /** Lembra a categoria depois que o pagamento deu certo. */
    const remember = () => {
        if (category?.value) {
            storeLastCategory(category);
        }
    };

    return {
        paymentDate, setPaymentDate, paymentMethod, setPaymentMethod, category, setCategory,
        reset, validate, toPayload, remember
    };
};

/**
 * Os três campos do pagamento, em colunas do grid do modal.
 *
 * @param {object} fields - O retorno de useEmployeePaymentFields.
 */
const EmployeePaymentFields = ({ fields }) => {
    const { t } = useTranslation();

    return (
        <>
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('cash_flow.employee_payment_date')} <AsteriskRequired /></FormLabel>
                    {/* Sem data futura: o pagamento é registrado depois que o dinheiro sai, e o
                        servidor recusa o contrário. */}
                    <Form.Control
                        type="date"
                        value={fields.paymentDate}
                        max={today()}
                        onChange={(event) => fields.setPaymentDate(event.target.value)}
                    />
                </FormGroup>
            </Col>
            <Col md={6}>
                <FormGroup>
                    <FormLabel>{t('payment_method')} <AsteriskRequired /></FormLabel>
                    <PaymentMethodSelect value={fields.paymentMethod} onChange={fields.setPaymentMethod} onlyMethods={ACCEPTED_METHODS} />
                </FormGroup>
            </Col>
            <Col xs={12}>
                <FormGroup>
                    <FormLabel>{t('cash_flow.employee_payment_category')} <AsteriskRequired /></FormLabel>
                    <CategorySelect value={fields.category} onChange={fields.setCategory} excludeDreGroups={['RECEITA']} />
                    <small className="text-muted d-block mt-1">{t('cash_flow.employee_payment_category_hint')}</small>
                </FormGroup>
            </Col>
        </>
    );
};

export default EmployeePaymentFields;
